/* eslint-disable max-len */
import { useContext, useEffect, useMemo, useState } from "react";
import { useScreenClass } from "react-grid-system";
import styled from "styled-components";
import Card from "../../components/Card";
import FlexBox, { FlexGrow } from "../../components/FlexBox";
import MuiIcon from "@mui/material/Icon";
import { RightAlign } from "../../components/UtilsComponents";
import { StyledText, TextSize, TextStatus } from "../../components/Text";
import CompetencyNavbar from "../../components/CompetencyNavbar";
import { useNavigate, useSearchParams } from "react-router-dom";
import { UserContext } from "../../context/UserContext";
import axios from "axios";
import { PillarType } from "../../types/Roadmap";
import LoadingSpinner from "../../components/LoadingSpinner";
import { getDomainWidth } from "../../hooks/generateMobileUi";
import theme from "../../styles/theme";
import { VersionContext } from "../../context/VersionContext";

export const DomainCard = styled(FlexBox)`
  padding: 16px;
  width: auto;
  height: 264px;
  background: ${theme.color.lightBlueGray200};
  color: ${theme.color.darkBlueGray900};
  border-radius: 8px;
  cursor: pointer;
  flex-wrap: nowrap;
`;
export const CustomDomainBlock = styled(FlexGrow)<{ blockWidth: string }>`
  flex-grow: 0;
  margin-bottom: 20px;
  max-width: ${({ blockWidth }) => blockWidth};
  min-width: ${({ blockWidth }) => blockWidth};
  min-height: 300px;
  max-height: 300px;
`;
export const StyledHr = styled.hr`
  width: 100%;
  border-color: #3c406c;
`;
function Domains() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const redirectDetail = (id: string, node: string) => {
    navigate(`/view/pillars/child?mode=${searchParams.get("mode")}`, {
      state: { id, node },
    });
  };
  const screenClass = useScreenClass();
  const [pillars, setPillars] = useState<PillarType>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const isMobile = useMemo(
    () => ["xs", "sm"].includes(screenClass),
    [screenClass]
  );
  const isTablet = useMemo(() => ["md"].includes(screenClass), [screenClass]);

  useEffect(() => {
    fetchCompetencies();
  }, []);
  const { version } = useContext(VersionContext);
  const fetchCompetencies = () => {
    setLoading(true);
    axios
      .get(`/pillar?curriculum_version=${version.version_number}`)
      .then((res) => {
        filterData(res.data.pillars);
      })
      .catch(() => {});
  };
  const filterData = (data: any) => {
    const custom: PillarType = [];
    data.forEach((element: any, index: any) => {
      custom.push({
        id: element.id,
        node: index + 1,
        code: element.code,
        prefix: element.prefix,
        label: element.title,
        description: element.description,
        iconBg: element.graphics.iconbg,
        borderColor: element.graphics.bordercolor,
        icon: element.graphics.icon,
        iconShadow: element.graphics.iconshadow,
        children: [],
      });
      if (element.subdomains && element.subdomains.length > 0) {
        element.subdomains.forEach((elementChild: any, indexChild: any) => {
          custom[index].children.push({
            id: elementChild.id,
            node: Number(
              String(data.length) + String(index + 1) + String(indexChild + 1)
            ),
            label: elementChild.title,
            count: "0/7",
            icon: "",
            description: elementChild.description,
          });
        });
      }
    });
    setPillars(custom);
    setLoading(false);
  };
  return (
    <>
      <CompetencyNavbar breadcrumb={[]} />
      <FlexBox alignItems="flex-start" style={{ marginRight: "-8px" }}>
        {loading ? (
          <LoadingSpinner />
        ) : (
          <>
            {pillars.map((item: any) => (
              <CustomDomainBlock
                blockWidth={getDomainWidth(isMobile, isTablet)}
              >
                <DomainCard
                  direction="column"
                  alignItems="flex-start"
                  onClick={() => {
                    redirectDetail(item.id, item.node);
                  }}
                >
                  <FlexGrow style={{ flexGrow: "0" }}>
                    <MuiIcon
                      sx={{
                        color: "white",
                        padding: "6px",
                        background: item.iconBg,
                        boxShadow: item.iconShadow,
                        borderRadius: "100px",
                        fontSize: "48px",
                      }}
                    >
                      {item.icon}
                    </MuiIcon>
                  </FlexGrow>
                  <FlexGrow style={{ flexGrow: "0" }}>
                    <StyledText
                      textSize={TextSize.paragraph}
                      fontWeight="600"
                      style={{ textTransform: "uppercase" }}
                    >
                      {item.label}
                    </StyledText>
                  </FlexGrow>
                  <FlexGrow style={{ overflow: "hidden" }}>
                    <StyledText
                      status={TextStatus.muted}
                      textSize={TextSize.default}
                      fontWeight="normal"
                    >
                      {item.description}
                    </StyledText>
                  </FlexGrow>
                  <FlexGrow
                    style={{ minWidth: "100%", flexGrow: "0", margin: "0" }}
                  >
                    <StyledHr />
                  </FlexGrow>
                  <FlexGrow
                    style={{ minWidth: "100%", flexGrow: "0", margin: "0" }}
                  >
                    <RightAlign>
                      <StyledText
                        status={TextStatus.muted}
                        textSize={TextSize.small}
                        fontWeight="normal"
                      >
                        {item?.children ? item.children.length : 0} domains
                      </StyledText>
                    </RightAlign>
                  </FlexGrow>
                </DomainCard>
              </CustomDomainBlock>
            ))}
          </>
        )}
      </FlexBox>
    </>
  );
}
export default Domains;
